import classNames from 'classnames';
import {BaseMap} from './BaseMap';
import {EditorEvent} from '../events';
import {EditorProps} from '../props';
import {createEditorActions} from '../actions';
import {ClientMode} from '../enums/ClientMode';

/**
 * Názvy synchronizovaných properties specifických pro mapový editor.
 *
 * @ignore
 */
const syncedPropNames = [
    'readOnly',
    'layersMode',
    'styles',
    'color',
    'colorScheme',
    'lineWidth',
    'tools',
    'contextTools',
    'keyboardShortcuts',
    'featureDeleteHandler',
    'featurePrintEnabled',
    'editableLayerIds',
];

/**
 * Mapový editor, který vyžaduje state na vstupu.
 *
 * @ignore
 */
export class BaseMapEditor extends BaseMap<EditorProps, EditorEvent> {
    /**
     * Vrátí jména properties, které synchronizujeme s mapovým serverem při změně.
     *
     * @returns Jména properties.
     */
    protected getSyncedPropNames(): string[] {
        return [...super.getSyncedPropNames(), ...syncedPropNames];
    }

    /**
     * Namapuje hodnotu synchronizované property.
     *
     * @param props - Properties ze kterých chceme vzít hodnotu.
     * @param name - Název property.
     * @returns Mapovaná hodnota property.
     */
    protected getSyncedPropValue(props, name): any {
        switch (name) {
            // Namísto callbacku, chceme předat pouze boolean informaci, že je callback zaregistrován.
            case 'featureDeleteHandler':
                return props[name] != null;

            default:
                return super.getSyncedPropValue(props, name);
        }
    }

    /**
     * Vrátí cestu mapové stránky v URL.
     *
     * @returns Cesta.
     */
    protected getPath(): string {
        return this.props.clientMode ?? ClientMode.EMBED_EDITOR;
    }

    /**
     * Provede výchozí synchronizaci properties.
     */
    protected postInitialSyncedProps(): void {
        super.postInitialSyncedProps();

        const {frameDispatch, initialFeatures, initialFeaturesOptions} = this.props;
        if (initialFeatures && initialFeatures.length) {
            createEditorActions(frameDispatch).loadFeatures(initialFeatures, initialFeaturesOptions);
        }
    }

    /**
     * Zpracuje nerozpoznanou zprávu od frame.
     *
     * @param message - Zpráva.
     */
    protected handleUnrecognizedMessage(message): void {
        switch (message.type) {
            case 'DELETE_FEATURES_REQUEST':
                this.props.featureDeleteHandler &&
                    this.props.featureDeleteHandler({
                        featurePointers: message.featurePointers,
                    });
                break;

            default:
                super.handleUnrecognizedMessage(message);
                break;
        }
    }

    /**
     * Vykreslí komponentu.
     *
     * @returns Element.
     */
    render(): JSX.Element | null {
        const {
            className: customClassName,
            readOnly,
            layersMode,
            styles,
            color,
            colorScheme,
            lineWidth,
            tools,
            contextTools,
            keyboardShortcuts,
            featureDeleteHandler,
            featurePrintEnabled,
            initialTopic,
            initialBackgroundLayer,
            initialForegroundLayer,
            initialLayers,
            initialCatalogNodes,
            initialLocation,
            initialZoom,
            initialFilter,
            initialFeatures,
            initialFeaturesOptions,
            clientMode,
            editableLayerIds,
            ...otherProps
        } = this.props;

        const className = classNames('react-mapio-map', 'react-mapio-map-editor', customClassName);
        return this.renderFrame({className, ...otherProps});
    }
}
